import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import {
	SiteFeatureConfigSymbol,
	IAppDidMountHandler,
	BrowserWindow,
	BrowserWindowSymbol,
	MasterPageFeatureConfigSymbol,
	CurrentRouteInfoSymbol,
	Props,
	IPropsStore,
} from '@wix/thunderbolt-symbols'
import type { QaApiMasterPageConfig, QaApiSiteConfig } from './types'
import { name } from './symbols'
import { ICurrentRouteInfo, IRouter, Router } from 'feature-router'
import { SessionManagerSymbol, ISessionManager } from 'feature-session-manager'
import { ILightbox, LightboxSymbol } from 'feature-lightbox'

const qaApiFactory = (
	siteFeatureConfig: QaApiSiteConfig,
	masterPageConfig: QaApiMasterPageConfig,
	window: NonNullable<BrowserWindow>,
	propsStore: IPropsStore,
	router: IRouter,
	currentRouteInfo: ICurrentRouteInfo,
	sessionManager: ISessionManager,
	popups?: ILightbox
): IAppDidMountHandler => {
	function getPrimaryPage() {
		const routeInfo = currentRouteInfo.getCurrentRouteInfo()
		const id = routeInfo ? routeInfo.pageId : null
		const { title } = id ? masterPageConfig.pagesDataItems[id] : { title: '' }
		return {
			id,
			title,
		}
	}

	return {
		async appDidMount() {
			// If window.qaApi is defined by the feature, don't initialize it again. Otherwise, override it.
			if (window.qaApi && 'siteIsReady' in window.qaApi) {
				return
			}

			window.qaApi = {
				siteIsReady: () => true,
				renderedWithThunderbolt: true,
				getClientSpecMap: () => siteFeatureConfig.clientSpecMap,
				getWixCodeModel: () => siteFeatureConfig.wixCodeModel,
				getPagesDataItems: () => masterPageConfig.pagesDataItems,
				getPrimaryPage,
				getRunningExperiments: () => siteFeatureConfig.runningExperiments,
				// not being destructed since we need the site config reference that is changed in editor
				getMetaSiteId: () => siteFeatureConfig.metaSiteId,
				getSiteInfo: () => siteFeatureConfig.siteInfo,
				getAppInstanceByAppDefId: (appDefId: string) => sessionManager.getAppInstanceByAppDefId(appDefId),
				getCompProps: (compId: string) => propsStore.get(compId),
				navigateToPage: ({ pageId }) => router.navigate(siteFeatureConfig.routes[pageId]),
				openPopup: ({ popupId }) => {
					if (popups && popups.isLightbox(popupId)) {
						return popups.open(popupId)
					} else {
						console.log('NOTE: popupId does not exist')
					}
				},
				getCurrentPopupId: () => {
					return popups ? popups.getCurrentLightboxId() : undefined
				},
			}
		},
	}
}

export const QaApi = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		Props,
		Router,
		CurrentRouteInfoSymbol,
		SessionManagerSymbol,
		optional(LightboxSymbol),
	],
	qaApiFactory
)
